import {
    Observable,
    map
} from "rxjs";

import {
    Injectable, inject
} from "@angular/core";
import {
    BulkResponse,
    BulkUtil,
    GetResponse,
    RequestUtil,
    SearchRequest,
    SearchResponse
} from "@shopliftr/common-js/shared";

import {
    InternalApiRequestService,
    IReadOnlyInternalApiClientService,
    IQueryInternalApiClientService,
    ListUtil
} from "@shopliftr/common-ng";

import {
    ModelAction, ISearchSort, SearchSortDirection
} from "@shopliftr/common-js/data";
import {
    ByodMarketArea, ByodMarketAreaSearchFilterOptions
} from "@shopliftr/common-js/smart";

/**
 * Service for handling calls to InternalApi relating to ByodMarketArea.
 *
 * This service implements the **Repository Pattern**, providing a clean abstraction layer
 * between the application's business logic and the data access layer. It encapsulates
 * all CRUD operations and search functionality for ByodMarketArea entities, delegating the
 * actual HTTP communication to the InternalApiRequestService.
 *
 * @pattern Repository Pattern
 * @implements {IReadOnlyInternalApiClientService<ByodMarketArea>}
 * @implements {IQueryInternalApiClientService<ByodMarketArea>}
 */
@Injectable({ providedIn: "root" })
export class ByodMarketAreaApiClientService implements IReadOnlyInternalApiClientService<ByodMarketArea>,
IQueryInternalApiClientService<ByodMarketArea> {
    /**
     * Base API path for all ByodMarketArea-related endpoints.
     * Used as the root path segment for constructing API URLs.
     */
    readonly basePath = "byod-market-area";

    /**
     * Injected service responsible for making HTTP requests to the Internal API.
     * Handles the low-level communication details including request formatting,
     * error handling, and response parsing.
     *
     * @private
     * @readonly
     */
    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Retrieves a single ByodMarketArea entity by its unique identifier.
     *
     * @param id - The unique identifier of the ByodMarketArea to retrieve
     * @returns Observable that emits the requested ByodMarketArea entity
     * @example
     * this.byodMarketAreaService.get('area-123').subscribe(area => {
     *   console.log(area.name);
     * });
     */
    get$(id: string): Observable<ByodMarketArea> {

        const request = RequestUtil.createGetRequest<ByodMarketArea>(id, ByodMarketArea.modelName, ModelAction.ClientGet);

        return this._internalApiRequestService.get$(request, this.basePath).pipe(map((response: GetResponse<ByodMarketArea>) => {

            return response.payload;
        }));
    }

    /**
     * Retrieves all ByodMarketArea entities matching the specified filter criteria.
     * Results are returned as a complete array without pagination.
     *
     * @param filterOptions - Filter criteria to apply to the search
     * @param [sort] - Optional sorting configuration. Defaults to ascending sort by name
     * @returns Observable that emits an array of all matching ByodMarketArea entities
     * @example
     * this.byodMarketAreaService.getAll({ chainId: 'chain-123' }).subscribe(areas => {
     *   console.log(`Found ${areas.length} market areas`);
     * });
     */
    getAll$(filterOptions: ByodMarketAreaSearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<ByodMarketArea>> {

        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch, ByodMarketArea.modelName, undefined, undefined, filterOptions, sort ?? [{
                field: "name",
                direction: SearchSortDirection.Ascending
            }]
        );

        return this._internalApiRequestService.getAll$<ByodMarketArea, ByodMarketAreaSearchFilterOptions>(request, this.basePath);
    }

    /**
     * Performs a paginated search for ByodMarketArea entities matching the specified criteria.
     * Returns results with pagination metadata for efficient data loading.
     *
     * @param filterOptions - Filter criteria to apply to the search
     * @param [page] - Zero-based page number for pagination
     * @param [pageSize] - Number of results per page
     * @param [sort] - Optional sorting configuration
     * @returns Observable that emits a SearchResponse containing matching entities and pagination metadata
     */
    search$(
        filterOptions: ByodMarketAreaSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<ByodMarketArea>> {

        const request: SearchRequest<ByodMarketAreaSearchFilterOptions> = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            ByodMarketArea.modelName,
            ListUtil.getFromValue(page, pageSize),
            pageSize,
            filterOptions,
            sort
        );

        return this._internalApiRequestService.search$<ByodMarketArea, ByodMarketAreaSearchFilterOptions>(request, this.basePath);
    }

    /** @deprecated Use search$ instead */
    searchCampaign$(
        filterOptions: ByodMarketAreaSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<ByodMarketArea>> {
        return this.search$(filterOptions, page, pageSize, sort);
    }

    /**
     * Retrieves multiple ByodMarketArea entities in a single bulk request.
     * More efficient than making individual get() calls when fetching multiple entities.
     *
     * @param ids - Array of unique identifiers for the ByodMarketAreas to retrieve
     * @returns Observable that emits a BulkResponse containing all requested entities
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<ByodMarketArea>(id, ByodMarketArea.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }
}
